import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { api, apiErrorMessage } from "../lib/api";
import { identify, track, reset as resetAnalytics } from "./../lib/analytics";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = anon, object = user
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);

  const checkSession = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      if (data.user?.id) identify(data.user.id, { username: data.user.username });
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  const login = useCallback(async (email, password) => {
    setError(null);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data.user);
      identify(data.user.id, { username: data.user.username });
      track("login", {});
      return data.user;
    } catch (e) {
      const msg = apiErrorMessage(e);
      setError(msg);
      throw new Error(msg);
    }
  }, []);

  const register = useCallback(async (payload) => {
    setError(null);
    try {
      const { data } = await api.post("/auth/register", payload);
      setUser(data.user);
      identify(data.user.id, { username: data.user.username });
      track("signup", { has_referral: !!payload.referral_code });
      return data.user;
    } catch (e) {
      const msg = apiErrorMessage(e);
      setError(msg);
      throw new Error(msg);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Logout failures aren't actionable for the user — drop their session
      // locally either way so the UI stays consistent.
      console.warn("logout request failed:", err?.message || err);
    }
    setUser(false);
    resetAnalytics();
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      return data.user;
    } catch {
      return null;
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, setUser, error, login, register, logout, refreshUser }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
